import { useForklifts } from '@/hooks/useForklifts'
import LoadingSpinner from '@/components/common/LoadingSpinner'
import WeeklyActivityCalendar from '@/components/dashboard/WeeklyActivityCalendar'
import { STATUS_COLORS, STATUS_LABELS } from '@/config/constants'
import type { ForkliftStatus } from '@/types/forklift'
import { Link } from 'react-router-dom'

export default function DashboardPage() {
  const { data: forkliftsData, isLoading, isFetching } = useForklifts()

  const forklifts = forkliftsData?.data || []

  // ステータス別カウント
  const statusCounts = forklifts.reduce(
    (acc, f) => {
      acc[f.status] = (acc[f.status] || 0) + 1
      return acc
    },
    {} as Record<ForkliftStatus, number>
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-bold text-gray-900">ダッシュボード</h1>
        {isFetching && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <LoadingSpinner size="sm" />
            <span>読み込み中...</span>
          </div>
        )}
      </div>

      {/* サマリーカード */}
      <div
        className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 ${isFetching ? 'opacity-60' : ''}`}
      >
        {isLoading ? (
          <>
            <div className="bg-primary-50 rounded-lg p-6 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-24 mb-4"></div>
              <div className="h-8 bg-gray-200 rounded w-12"></div>
            </div>
            <div className="bg-green-50 rounded-lg p-6 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-16 mb-4"></div>
              <div className="h-8 bg-gray-200 rounded w-12"></div>
            </div>
            <div className="bg-yellow-50 rounded-lg p-6 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-16 mb-4"></div>
              <div className="h-8 bg-gray-200 rounded w-12"></div>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-20 mb-4"></div>
              <div className="h-8 bg-gray-200 rounded w-12"></div>
            </div>
          </>
        ) : (
          <>
            <SummaryCard
              title="総フォークリフト数"
              value={forklifts.length}
              color="text-primary-600"
              bgColor="bg-primary-50"
            />
            <SummaryCard
              title="稼働中"
              value={statusCounts.operating || 0}
              color="text-green-600"
              bgColor="bg-green-50"
            />
            <SummaryCard
              title="アイドル"
              value={statusCounts.idle || 0}
              color="text-yellow-600"
              bgColor="bg-yellow-50"
            />
            <SummaryCard
              title="オフライン"
              value={statusCounts.offline || 0}
              color="text-gray-600"
              bgColor="bg-gray-50"
            />
          </>
        )}
      </div>

      {/* フォークリフトステータス */}
      <div
        className={`bg-white rounded-lg shadow p-6 ${isFetching ? 'opacity-60' : ''}`}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            稼働中フォークリフト
          </h2>
          <Link
            to="/forklifts"
            className="text-sm text-primary-600 hover:text-primary-800"
          >
            すべて表示 →
          </Link>
        </div>
        {isLoading ? (
          <div className="flex flex-col items-center justify-center h-48 gap-2">
            <LoadingSpinner size="lg" />
            <span className="text-gray-600">データを取得中...</span>
          </div>
        ) : forklifts.filter((f) => f.status === 'operating').length === 0 ? (
          <div className="flex items-center justify-center h-48 text-gray-500">
            稼働中のフォークリフトがありません
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {[...forklifts]
              .filter((f) => f.status === 'operating')
              .sort(
                (a, b) =>
                  new Date(b.last_seen).getTime() -
                  new Date(a.last_seen).getTime()
              )
              .map((forklift) => (
                <Link
                  key={forklift.fork_id}
                  to={`/forklifts/${forklift.fork_id}`}
                  className="flex items-center justify-between gap-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 truncate">
                      {forklift.fork_id}
                    </p>
                    <p className="text-sm text-gray-500 truncate">
                      {forklift.site_id}
                    </p>
                  </div>
                  <span
                    className="shrink-0 px-3 py-1 rounded-full text-sm font-medium"
                    style={{
                      backgroundColor: `${STATUS_COLORS[forklift.status]}20`,
                      color: STATUS_COLORS[forklift.status],
                    }}
                  >
                    {STATUS_LABELS[forklift.status]}
                  </span>
                </Link>
              ))}
          </div>
        )}
      </div>

      {/* 週次稼働状況カレンダー */}
      <WeeklyActivityCalendar />
    </div>
  )
}

interface SummaryCardProps {
  title: string
  value: number
  color: string
  bgColor: string
}

function SummaryCard({ title, value, color, bgColor }: SummaryCardProps) {
  return (
    <div className={`${bgColor} rounded-lg p-6`}>
      <p className="text-sm font-medium text-gray-600">{title}</p>
      <p className={`text-3xl font-bold ${color} mt-2`}>{value}</p>
    </div>
  )
}
